import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDuration, fmtTime, formatApiError, fyBounds } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Download, Filter, ChevronDown, Pencil, Trash2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { useTags } from "../context/TagsContext";

// ===== TIMEZONE HELPERS FOR EXPORT =====
// Returns timezone offset in minutes (e.g., -330 for India UTC+5:30, +300 for EST UTC-5)
const getTimezoneOffset = () => new Date().getTimezoneOffset();

// Returns IANA timezone name (e.g., "Asia/Kolkata", "America/New_York")
const getTimezoneName = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Creates export filters with timezone info
const getExportFilters = (baseFilters) => ({
  ...baseFilters,
  timezone_offset: getTimezoneOffset(),
  timezone_name: getTimezoneName(),
});
// ========================================

// Finds the correct rate from the array based on the task date
const getRateForDate = (ratesArray, taskDate) => {
    if (!ratesArray || ratesArray.length === 0) return 0;
    
    // Extract just "YYYY-MM-DD" (in case taskDate has time attached like "2024-05-15T14:00:00")
    const dateStr = taskDate.substring(0, 10);

    // Find the FY where the task date falls
    for (const r of ratesArray) {
        if (r.start_date <= dateStr && dateStr <= r.end_date) {
            return r.rate || 0;
        }
    }
    
    // Fallback: If the date is outside all ranges, use the most recently added rate
    return ratesArray[ratesArray.length - 1]?.rate || 0;
};
const GREYS = ["#365314", "#4D7C0F", "#65A30D", "#84CC16", "#A3E635", "#BEF264", "#D9F99D", "#ECFCCB"];
const LIME = "#84CC16";

// ===== TIMEZONE FIX UTILITIES =====
const toLocalDateString = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalDateFromTimestamp = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  return toLocalDateString(d);
};

// ==========================================
// UPDATE triggerDownload to accept a filename
// ==========================================
const triggerDownload = (blobData, kind, filename) => {
  const blob = new Blob([blobData], {
    type: kind === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "application/pdf",
  });
  
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Use provided filename, or fallback to default
  a.download = filename || `esstrack-report-${new Date().toISOString().slice(0, 10)}.${kind === "xlsx" ? "xlsx" : "pdf"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  
  setTimeout(() => window.URL.revokeObjectURL(url), 1000); 
};


// ==========================================
function MultiSelect({ label, options, value, onChange, getKey, getLabel, testid }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);
  
  useEffect(() => {
    if (!open) {
      setSearch(""); // Clear search when closing
      return;
    }
    
    // Auto-focus search input when dropdown opens
    setTimeout(() => searchRef.current?.focus(), 0);

    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
    // Filter options based on search term
  const filteredOptions = options.filter((o) =>
    getLabel(o).toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="border border-gray-200 px-3 py-2 text-sm flex items-center gap-2 min-w-[140px] justify-between" data-testid={testid}>
        <span className="truncate">{value.length === 0 ? label : `${label} (${value.length})`}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 w-72 max-h-64 overflow-y-auto bg-white border border-gray-200 z-30 shadow-md">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="w-full border border-gray-200 px-3 py-1.5 text-sm bg-white"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking input
            />
          </div>
          
          {/* Scrollable Options List */}
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 && (
              <div className="px-3 py-6 text-sm text-gray-400 text-center">
                No matches found
              </div>
            )}
            {filteredOptions.map((o) => {
            const k = getKey(o); const l = getLabel(o);
            return (
              <label key={k} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={value.includes(k)}
                  onChange={(e) => onChange(e.target.checked ? [...value, k] : value.filter((x) => x !== k))}
                />
                 <span className="truncate">{l}</span>
              </label>
            );
          })}
        </div>
          <div className="p-2 border-t flex justify-between text-xs">
            <button onClick={() => onChange([])} className="uppercase tracking-wider">Clear</button>
            <button onClick={() => setOpen(false)} className="uppercase tracking-wider">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

const SortHeader = ({ label, sortKey, currentSort, onSort, className = "" }) => {
  const isActive = currentSort.key === sortKey;
  const isDesc = isActive && currentSort.dir === "desc";
  const isAsc = isActive && currentSort.dir === "asc";
  const isRight = className.includes("text-right");

  return (
    <th 
      className={`${className} cursor-pointer select-none hover:text-[var(--ess-ink)]`}
      onClick={() => {
        if (isActive) {
          onSort({ key: sortKey, dir: currentSort.dir === "desc" ? "asc" : "desc" });
        } else {
          onSort({ key: sortKey, dir: "desc" });
        }
      }}
    >
      <div className={`inline-flex items-center gap-1 ${isRight ? 'flex-row-reverse' : ''}`}>
        <span>{label}</span>
        <span className="inline-flex flex-col leading-[10px]">
          {/* ✅ Changed from text-gray-300 to text-gray-500 and text-[8px] to text-[10px] so it's visible */}
          <span className={`text-[10px] ${isAsc ? "text-[var(--ess-ink)] font-bold" : "text-gray-300"}`}>▲</span>
          <span className={`text-[10px] ${isDesc ? "text-[var(--ess-ink)] font-bold" : "text-gray-300"}`}>▼</span>
        </span>
      </div>
    </th>
  );
};

export default function ReportsPage() {
  const { user } = useAuth();
  const { tags: PREDEFINED_TAGS } = useTags(); // Get dynamic tags
    const isExporting = useRef(false);
  const isAdmin = user?.role === "admin";
  
  // ✅ Helper to get the current week (Monday to Sunday)
  const getCurrentWeekBounds = () => {
    const now = new Date();
    const day = now.getDay(); // 0 (Sunday) to 6 (Saturday)
    
    // Calculate the date for Monday
    // If today is Sunday (0), subtract 6 days. Otherwise, subtract (day - 1) days.
    const diffToMonday = now.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(now.setDate(diffToMonday));
    
    // Calculate the date for Sunday (Monday + 6 days)
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return {
      start: toLocalDateString(monday),
      end: toLocalDateString(sunday)
    };
  };

  const weekBounds = getCurrentWeekBounds();
  
  const [view, setView] = useState("summary");
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    user_ids: [], project_ids: [], clients: [], tags: [],
    start_date: weekBounds.start, end_date: weekBounds.end, // ✅ Uses current week instead of FY
    description_search: "", studio_head: "",
  });
  // Add studio head options
const studioHeads = useMemo(() => 
  Array.from(new Set(projects.map((p) => p.studio_head).filter(Boolean))).sort(), 
  [projects]
  );
  const [projectSort, setProjectSort] = useState({ key: "duration_seconds", dir: "desc" });
  const [userSort, setUserSort] = useState({ key: "duration_seconds", dir: "desc" });
  const [detailSort, setDetailSort] = useState({ key: "start_time", dir: "desc" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const clients = useMemo(() => Array.from(new Set(projects.map((p) => p.client))).filter(Boolean), [projects]);

  const projectMap = useMemo(() => {
    const m = {}; for (const p of projects) m[p.id] = p; return m;
  }, [projects]);
  const userRatesMap = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.billable_rates || [];
    return m;
  }, [users]);
  const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

  // Aggregate billable seconds & amount and per-project amount (admin only — non-admin doesn't have rateMap data)
  const billing = useMemo(() => {
    if (!data) return null;
    let billableSecs = 0;
    let totalAmount = 0;
    const projectAmount = {};
    //for (const e of data.entries) {
    const validEntries = (data.entries || []).filter(e => e.project_name && e.project_name.trim() !== "");
    for (const e of validEntries) {
      const proj = projectMap[e.project_id];
      const isBillable = !!proj?.billable;
      if (isBillable) {
        billableSecs += e.duration_seconds;
        const ratesArray = userRatesMap[e.user_id];
        const correctRate = getRateForDate(ratesArray, getLocalDateFromTimestamp(e.start_time));
        const amt = (e.duration_seconds / 3600) * correctRate;
        totalAmount += amt;
        projectAmount[e.project_id] = (projectAmount[e.project_id] || 0) + amt;
      }
    }
    return { billableSecs, totalAmount, projectAmount };
  }, [data, projectMap, userRatesMap]);

    // Updated sortedProjects useMemo with string sorting support
// Update sortedProjects to support studio_head sorting
const sortedProjects = useMemo(() => {
  const projects = (data?.by_project || []).filter(p => p.project_name && p.project_name.trim() !== "");
  return [...projects].sort((a, b) => {
    let valA, valB;
    
    if (projectSort.key === "amount") {
      valA = billing?.projectAmount?.[a.project_id] || 0;
      valB = billing?.projectAmount?.[b.project_id] || 0;
      return projectSort.dir === "desc" ? valB - valA : valA - valB;
    } else if (projectSort.key === "duration_seconds") {
      valA = a[projectSort.key] || 0;
      valB = b[projectSort.key] || 0;
      return projectSort.dir === "desc" ? valB - valA : valA - valB;
    } else {
      // Handle String sorting for project_name, client, and studio_head
      valA = (a[projectSort.key] || "").toString().toLowerCase();
      valB = (b[projectSort.key] || "").toString().toLowerCase();
      return projectSort.dir === "desc" ? valB.localeCompare(valA) : valA.localeCompare(valB);
    }
  });
}, [data?.by_project, projectSort, billing]);

    const sortedUsers = useMemo(() => {
    const users = data?.by_user || [];
    //const entries = data?.entries || [];
    const entries = (data?.entries || []).filter(e => e.project_name && e.project_name.trim() !== "");
    const usersWithAmt = users.map(u => {
      let amt = 0;
      if (isAdmin && billing) {
        for (const e of entries) {
          if (e.user_id !== u.user_id) continue;
          const proj = projectMap[e.project_id];
          const ratesArray = userRatesMap[u.user_id];
          const correctRate = getRateForDate(ratesArray, getLocalDateFromTimestamp(e.start_time));
          if (proj?.billable) amt += (e.duration_seconds / 3600) * correctRate;
        }
      }
      return { ...u, calculated_amount: amt };
    });

    return usersWithAmt.sort((a, b) => {
      let valA, valB;
      if (userSort.key === "amount") {
        valA = a.calculated_amount;
        valB = b.calculated_amount;
      } else {
        valA = a[userSort.key] || 0;
        valB = b[userSort.key] || 0;
      }
      return userSort.dir === "desc" ? valB - valA : valA - valB;
    });
  }, [data?.by_user, data?.entries, userSort, billing, isAdmin, projectMap, userRatesMap]);

    // ... existing sortedProjects and sortedUsers hooks ...

   const sortedEntries = useMemo(() => {
    const entries = (data?.entries || []).filter(e => e.project_name && e.project_name.trim() !== "");
    const entriesWithAmt = entries.map(e => {
      let amt = 0;
      if (isAdmin) {
        const proj = projectMap[e.project_id];
        const ratesArray = userRatesMap[e.user_id];
        const correctRate = getRateForDate(ratesArray, getLocalDateFromTimestamp(e.start_time));
        if (proj?.billable) amt = (e.duration_seconds / 3600) * correctRate;
      }
      return { ...e, calculated_amount: amt, local_date: getLocalDateFromTimestamp(e.start_time) || e.date };
    });

    return entriesWithAmt.sort((a, b) => {
      let valA, valB;
      
      if (detailSort.key === "amount") {
        valA = a.calculated_amount;
        valB = b.calculated_amount;
        return detailSort.dir === "desc" ? valB - valA : valA - valB;
      } else if (detailSort.key === "date") {
        valA = a.local_date || "";
        valB = b.local_date || "";
        return detailSort.dir === "desc" ? valB.localeCompare(valA) : valA.localeCompare(valB);
      } else if (detailSort.key === "start_time") {
        valA = a[detailSort.key] || "";
        valB = b[detailSort.key] || "";
        return detailSort.dir === "desc" ? valB.localeCompare(valA) : valA.localeCompare(valB);
      } else if (detailSort.key === "duration_seconds") {
        valA = a[detailSort.key] || 0;
        valB = b[detailSort.key] || 0;
        return detailSort.dir === "desc" ? valB - valA : valA - valB;
      } else {
        // ✅ NEW: Handle String sorting for Project, Client, User, Description, Tags
        valA = (a[detailSort.key] || "").toString().toLowerCase();
        valB = (b[detailSort.key] || "").toString().toLowerCase();
        return detailSort.dir === "desc" ? valB.localeCompare(valA) : valA.localeCompare(valB);
      }
    });
  }, [data?.entries, detailSort, isAdmin, projectMap, userRatesMap]);
  
  // Admin-only: edit/delete an entry from the Detailed view
  const [editing, setEditing] = useState(null);
  const onDeleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/time-entries/${id}`);
      toast.success("Deleted");
      run();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/projects")])
      .then(([u, p]) => { setUsers(u.data); setProjects(p.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reports/run", filters);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
  
    const exportFile = async (kind) => {
      // ✅ BLOCK IF ALREADY EXPORTING
  if (isExporting.current) return;
  isExporting.current = true;

  // ✅ SAFE HELPER: Takes filters & projects as arguments to avoid "not defined" errors
  const getFilename = (kind, f, projs) => {
    const ext = kind === "xlsx" ? "xlsx" : "pdf";
    const defaultName = `esstrack-report-${new Date().toISOString().slice(0, 10)}.${ext}`;

    let monthYear = "";
    if (f.start_date) {
      try {
        const d = new Date(f.start_date + "T00:00:00");
        if (!isNaN(d.getTime())) {
          monthYear = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        }
      } catch (e) {}
    }

    if (!monthYear) return defaultName;

    let suffix = "";
    if (f.clients && f.clients.length === 1) {
      suffix = `_${f.clients[0].trim()}`;
    } else if (f.project_ids && f.project_ids.length === 1) {
      const proj = projs.find(p => p.id === f.project_ids[0]);
      if (proj) {
        suffix = `_${proj.name.trim()}`;
      }
    }

    return `esstrack-report-${monthYear}${suffix}.${ext}`;
  };

  const exportFilters = {
    ...filters,
    timezone_offset: new Date().getTimezoneOffset(),
    timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  try {
    toast.loading(`Starting ${kind.toUpperCase()} generation...`, { id: "export-toast" });
    
    const r = await api.post(`/reports/export/${kind}`, exportFilters, { 
      responseType: "blob" 
    });
    
    // ✅ FIX: Convert to ArrayBuffer (preserves binary perfectly)
    const arrayBuffer = await r.data.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Safely peek at first 100 bytes to check if it's JSON
    const peekString = new TextDecoder().decode(uint8Array.slice(0, 100));
    let jsonData = null;
    try {
      if (peekString.trimStart().startsWith("{")) {
        const fullText = new TextDecoder().decode(uint8Array);
        jsonData = JSON.parse(fullText);
      }
    } catch (e) {}
    
    if (jsonData) {
      if (jsonData.job_id) {
        toast.loading(`Generating ${kind.toUpperCase()} in background. This may take a minute...`, { id: "export-toast" });
        
        const pollStatus = async () => {
          try {
            const statusRes = await api.get(`/reports/export/status/${jsonData.job_id}`, {
              responseType: 'blob'
            });
            
            const statusBuffer = await statusRes.data.arrayBuffer();
            const statusUint8 = new Uint8Array(statusBuffer);
            const statusPeek = new TextDecoder().decode(statusUint8.slice(0, 100));
            let statusJson = null;
            
            try {
              if (statusPeek.trimStart().startsWith("{")) {
                const fullStatusText = new TextDecoder().decode(statusUint8);
                statusJson = JSON.parse(fullStatusText);
              }
            } catch (e) {}
            
            if (statusJson) {
              if (statusJson.detail || statusJson.status === 'failed') {
                toast.error(`Export failed: ${statusJson.detail || statusJson.error || 'Unknown error'}`, { id: "export-toast" });
                isExporting.current = false;
              } else {
                setTimeout(pollStatus, 3000);
              }
            } else {
              // FILE IS READY!
              const fileBlob = new Blob([statusBuffer], {
                type: kind === "xlsx"
                  ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  : "application/pdf",
              });
              const filename = getFilename(kind, filters, projects); // ✅ Pass state here
              triggerDownload(fileBlob, kind, filename);
              toast.success(`${kind.toUpperCase()} downloaded successfully!`, { id: "export-toast" });
              isExporting.current = false;
            }
          } catch (pollError) {
            toast.error("Failed to check export status", { id: "export-toast" });
            isExporting.current = false;
          }
        };
        
        setTimeout(pollStatus, 3000);
        
      } else if (jsonData.detail) {
        throw new Error(jsonData.detail);
      } else {
        throw new Error("Unexpected response from server");
      }
    } 
    else {
      // SMALL FILE: Use untouched ArrayBuffer
      const fileBlob = new Blob([arrayBuffer], {
        type: kind === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const filename = getFilename(kind, filters, projects); // ✅ Pass state here
      triggerDownload(fileBlob, kind, filename);
      toast.success(`${kind.toUpperCase()} downloaded successfully!`, { id: "export-toast" });
      isExporting.current = false;
    }
    
  } catch (e) {
    let msg = "Export failed";
    if (e.message && !e.message.includes("Network Error")) {
      msg = e.message;
    } else {
      try {
        if (e.response?.data) {
          const text = typeof e.response.data === 'string' 
            ? e.response.data 
            : await e.response.data.text();
          const j = JSON.parse(text);
          msg = j.detail || j.message || msg;
        }
      } catch { }
    }
    toast.error(msg, { id: "export-toast" });
    isExporting.current = false;
  }
};
  const dailyChart = useMemo(() => {
    // ✅ FIX: Use the pre-calculated 'by_day' from MongoDB aggregation instead of looping through limited 'entries'
    if (!data?.by_day) return [];
    return data.by_day
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({ 
        date: d.date, 
        hours: +(d.duration_seconds / 3600).toFixed(2) 
      }));
  }, [data?.by_day]);
  //const projectPie = (data?.by_project || []).map((p) => ({ name: p.project_name, value: p.duration_seconds }));
  const validProjects = (data?.by_project || []).filter(p => p.project_name && p.project_name.trim() !== "");
  const projectPie = validProjects.map((p) => ({ name: p.project_name, value: p.duration_seconds }));
  const tagPie = (data?.by_tag || []).map((t) => ({ name: t.tag, value: t.duration_seconds }));

  return (
    <div data-testid="reports-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex flex-wrap items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3 justify-between gap-3">
        <div>
          <div className="ess-section-label">Analyze</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Time Report</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportFile("pdf")} className="border border-gray-200 px-4 py-2 text-sm flex items-center gap-2 hover:border-[var(--ess-ink)]" data-testid="export-pdf">
            <Download size={14} /> PDF
          </button>
          <button onClick={() => exportFile("xlsx")} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm flex items-center gap-2" data-testid="export-xlsx">
            <Download size={14} /> Export XLSX
          </button>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-[var(--ess-surface)] px-4 sm:px-8 py-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 mr-3">
            <button onClick={() => setView("summary")} className={`px-3 py-1.5 text-sm uppercase tracking-wider ${view === "summary" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`} data-testid="view-summary">Summary</button>
            <button onClick={() => setView("detailed")} className={`px-3 py-1.5 text-sm uppercase tracking-wider ${view === "detailed" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`} data-testid="view-detailed">Detailed</button>
            <button onClick={() => setView("weekly")} className={`px-3 py-1.5 text-sm uppercase tracking-wider ${view === "weekly" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`} data-testid="view-weekly">Weekly</button>
          </div>

          <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-start" />
          <span className="text-gray-400">to</span>
          <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-end" />

          <MultiSelect testid="filter-team" label="Team" options={users} value={filters.user_ids} onChange={(v) => setFilters({ ...filters, user_ids: v })} getKey={(u) => u.id} getLabel={(u) => u.name} />
          <MultiSelect testid="filter-project" label="Project" options={projects} value={filters.project_ids} onChange={(v) => setFilters({ ...filters, project_ids: v })} getKey={(p) => p.id} getLabel={(p) => p.name} />
          <MultiSelect testid="filter-client" label="Client" options={clients.map((c) => ({ k: c }))} value={filters.clients} onChange={(v) => setFilters({ ...filters, clients: v })} getKey={(o) => o.k} getLabel={(o) => o.k} />
          {/* ADD STUDIO HEAD FILTER */}
    <select 
      value={filters.studio_head} 
      onChange={(e) => setFilters({ ...filters, studio_head: e.target.value })} 
      className="border border-gray-200 px-3 py-2 text-sm min-w-[140px]" 
      data-testid="filter-studio-head"
    >
      <option value="">All Studio Heads</option>
      {studioHeads.map((sh) => <option key={sh} value={sh}>{sh}</option>)}
    </select>
          <MultiSelect testid="filter-tag" label="Tag" options={PREDEFINED_TAGS.map((t) => ({ k: t }))} value={filters.tags} onChange={(v) => setFilters({ ...filters, tags: v })} getKey={(o) => o.k} getLabel={(o) => o.k} />

          <input
            value={filters.description_search}
            onChange={(e) => setFilters({ ...filters, description_search: e.target.value })}
            placeholder="Description contains..."
            className="border border-gray-200 px-3 py-2 text-sm flex-1 min-w-[200px]"
            data-testid="filter-description"
          />

          <button onClick={run} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider flex items-center gap-2" data-testid="apply-filter">
            <Filter size={14} /> Apply
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        {loading && <div className="text-sm text-gray-500">Loading...</div>}

        {data && (
          <>
            <div className="mb-6 flex flex-wrap items-baseline gap-x-8 gap-y-2">
              <div className="flex items-baseline gap-3">
                <div className="ess-section-label">Total</div>
                <div className="text-3xl font-medium tabular-nums text-[var(--ess-ink)]" data-testid="report-total">{fmtDuration(data.total_seconds)}</div>
              </div>
              {isAdmin && billing && (
                <>
                  <div className="flex items-baseline gap-3">
                    <div className="ess-section-label">Billable</div>
                    <div className="text-2xl font-medium tabular-nums text-[var(--ess-ink)]" data-testid="report-billable">{fmtDuration(billing.billableSecs)}</div>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <div className="ess-section-label">Amount</div>
                    <div className="text-2xl font-semibold tabular-nums text-[var(--ess-ink)]" data-testid="report-amount">{fmtINR(billing.totalAmount)}</div>
                  </div>
                </>
              )}
            </div>

                        {view === "summary" && (
              <>
                {/* ... Daily Breakdown & Pie Charts remain the same ... */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <div className="lg:col-span-2 border border-gray-200 p-4">
                    <div className="ess-section-label mb-3">Daily breakdown</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={dailyChart}>
                        <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#6B7280" />
                        <YAxis tick={{ fontSize: 10 }} stroke="#6B7280" />
                        <Tooltip formatter={(v) => `${v}h`} />
                        <Bar dataKey="hours" fill={LIME} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="border border-gray-200 p-4">
                    <div className="ess-section-label mb-3">By project</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={projectPie} dataKey="value" nameKey="name" outerRadius={90}>
                          {projectPie.map((_, i) => <Cell key={i} fill={GREYS[i % GREYS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtDuration(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

{/* BY PROJECT TABLE */}
<div className="border border-gray-200 mb-8 overflow-x-auto">
  <table className="ess-table w-full min-w-[640px]">
    <thead>
      <tr>
        <SortHeader label="Project" sortKey="project_name" currentSort={projectSort} onSort={setProjectSort} className="px-3 py-2 text-left" />
        <SortHeader label="Client" sortKey="client" currentSort={projectSort} onSort={setProjectSort} className="px-3 py-2 text-left" />
        <SortHeader label="Studio Head" sortKey="studio_head" currentSort={projectSort} onSort={setProjectSort} className="px-3 py-2 text-left" />
        <SortHeader label="Duration" sortKey="duration_seconds" currentSort={projectSort} onSort={setProjectSort} className="w-28 px-3 py-2 text-right tabular-nums" />
        {isAdmin && <SortHeader label="Amount" sortKey="amount" currentSort={projectSort} onSort={setProjectSort} className="w-32 px-3 py-2 text-right tabular-nums" />}
      </tr>
    </thead>
    <tbody>
      {sortedProjects.map((p, i) => (
        <tr key={i} data-testid={`row-project-${i}`}>
          <td className="px-3 py-2 font-medium text-[var(--ess-ink)]">{p.project_name}</td>
          <td className="px-3 py-2">{p.client}</td>
          <td className="px-3 py-2">{p.studio_head || "—"}</td>  {/* ADD THIS */}
          <td className="w-28 px-3 py-2 text-right tabular-nums">{fmtDuration(p.duration_seconds)}</td>
          {isAdmin && (
            <td className="w-32 px-3 py-2 text-right tabular-nums">
              {fmtINR(billing?.projectAmount?.[p.project_id] || 0)}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  </table>
</div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* TEAM MEMBER TABLE */}
                  <div className="border border-gray-200 overflow-x-auto">
                    <table className="ess-table w-full min-w-[480px]">
                      <thead><tr>
                        <th className="px-3 py-2 text-left">Team Member</th>
                        {/* Updated Headers with Sorting */}
                        <SortHeader label="Duration" sortKey="duration_seconds" currentSort={userSort} onSort={setUserSort} widthClass="w-28" />
                        {isAdmin && <SortHeader label="Amount" sortKey="amount" currentSort={userSort} onSort={setUserSort} widthClass="w-32" />}
                      </tr></thead>
                      <tbody>
                        {/* Updated to use sortedUsers and pre-calculated amount */}
                        {sortedUsers.map((u) => (
                          <tr key={u.user_id}>
                            <td className="px-3 py-2 font-medium text-[var(--ess-ink)]">{u.user_name}</td>
                            <td className="w-28 px-3 py-2 text-right tabular-nums">{fmtDuration(u.duration_seconds)}</td>
                            {isAdmin && <td className="w-32 px-3 py-2 text-right tabular-nums">{fmtINR(u.calculated_amount)}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="border border-gray-200 p-4">
                    <div className="ess-section-label mb-3">By tag</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={tagPie} dataKey="value" nameKey="name" outerRadius={80}>
                          {tagPie.map((_, i) => <Cell key={i} fill={GREYS[i % GREYS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtDuration(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
            {view === "weekly" && <WeeklyView data={data} />}
            
            {view === "detailed" && (
              <div className="border border-gray-200 overflow-x-auto">
                <table className="ess-table w-full min-w-[1200px]">
                  <thead>
                    <tr>
                      <SortHeader label="Date" sortKey="date" currentSort={detailSort} onSort={setDetailSort} className="px-3 py-2 text-left" />
                      <th className="px-3 py-2 text-left">User</th>
                      <SortHeader label="Project" sortKey="project_name" currentSort={detailSort} onSort={setDetailSort} className="px-3 py-2 text-left" />
                      <SortHeader label="Client" sortKey="project_client" currentSort={detailSort} onSort={setDetailSort} className="px-3 py-2 text-left" />
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-left">Tags</th>
                      <SortHeader label="Time" sortKey="start_time" currentSort={detailSort} onSort={setDetailSort} className="px-3 py-2 text-left" />
                      <SortHeader label="Duration" sortKey="duration_seconds" currentSort={detailSort} onSort={setDetailSort} className="w-28 px-3 py-2 text-right tabular-nums" />
                      {isAdmin && <SortHeader label="Amount" sortKey="amount" currentSort={detailSort} onSort={setDetailSort} className="w-32 px-3 py-2 text-right tabular-nums" />}
                      {(isAdmin || sortedEntries.some(e => e.user_id === user?.id)) && <th className="w-20"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEntries.map((e) => {
                      const canEdit = e.user_id === user?.id || isAdmin;
                      const canDelete = isAdmin; 
                      
                      return (
                        <tr key={e.id} data-testid={`detailed-row-${e.id}`}>
                          <td>{e.local_date || e.date}</td>
                          <td>{e.user_name}</td>
                          <td className="font-medium text-[var(--ess-ink)]">{e.project_name}</td>
                          <td>{e.project_client}</td>
                          <td>{e.description}</td>
                          <td className="text-xs">{e.tags.join(", ")}</td>
                          <td className="text-xs tabular-nums">{fmtTime(e.start_time)} – {fmtTime(e.end_time)}</td>
                          <td className="text-right tabular-nums">{fmtDuration(e.duration_seconds)}</td>
                          {isAdmin && <td className="text-right tabular-nums">{fmtINR(e.calculated_amount)}</td>}
                          {canEdit && (
                            <td className="text-right whitespace-nowrap">
                              <button 
                                onClick={() => setEditing(e)} 
                                className="text-gray-500 hover:text-[var(--ess-ink)] mr-2" 
                                data-testid={`edit-detailed-${e.id}`}
                              >
                                <Pencil size={14} />
                              </button>
                              {canDelete && (
                                <button 
                                  onClick={() => onDeleteEntry(e.id)} 
                                  className="text-gray-400 hover:text-red-600" 
                                  data-testid={`delete-detailed-${e.id}`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {sortedEntries.length === 0 && (
                      <tr><td colSpan={isAdmin ? "10" : "8"} className="text-center py-10 text-gray-500">No entries match these filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
          </>
        )}
      </div>

                  {editing && (
        <ReportEditEntryModal
          entry={editing}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); run(); }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
}

function ReportEditEntryModal({ entry, projects, onClose, onSaved, isAdmin }) {
  const [desc, setDesc] = useState(entry.description);
  const [projectId, setProjectId] = useState(entry.project_id);
  const { tags: PREDEFINED_TAGS_FULL } = useTags();
  const [tags, setTags] = useState(entry.tags || []);
  const [date, setDate] = useState(() => getLocalDateFromTimestamp(entry.start_time) || entry.date);
  const [start, setStart] = useState(new Date(entry.start_time).toTimeString().slice(0, 5));
  const [end, setEnd] = useState(new Date(entry.end_time).toTimeString().slice(0, 5));
  const [saving, setSaving] = useState(false);

  const combine = (d, hm) => {
    const [h, m] = hm.split(":").map(Number);
    const dt = new Date(d + "T00:00:00");
    dt.setHours(h, m, 0, 0);
    return dt.toISOString();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tags.length === 0) return toast.error("At least one tag required");
    setSaving(true);
    try {
      await api.put(`/time-entries/${entry.id}`, {
        description: desc.trim(),
        project_id: projectId,
        tags,
        start_time: combine(date, start),
        end_time: combine(date, end),
      });
      toast.success("Updated");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="report-edit-entry-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="ess-section-label">{isAdmin ? "Admin override" : "Edit entry"}</div>
            <h3 className="text-lg font-semibold">{isAdmin ? `Edit entry — ${entry.user_name}` : "Edit time entry"}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="ess-section-label block mb-1">Description</label>
            <input 
              value={desc} 
              onChange={(e) => setDesc(e.target.value)} 
              required 
              className="w-full border border-gray-200 px-3 py-2 text-sm" 
              data-testid="r-edit-desc" 
            />
          </div>
          <div>
            <label className="ess-section-label block mb-1">Project</label>
            <select 
              value={projectId} 
              onChange={(e) => setProjectId(e.target.value)} 
              required 
              className="w-full border border-gray-200 px-3 py-2 text-sm" 
              data-testid="r-edit-project"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.client}{!p.active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ess-section-label block mb-1">Tags</label>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_TAGS_FULL.map((t) => (
                <label 
                  key={t} 
                  className={`text-xs uppercase tracking-wider px-2 py-1 cursor-pointer border ${
                    tags.includes(t) 
                      ? "bg-[var(--ess-ink)] text-white border-[var(--ess-ink)]" 
                      : "border-gray-200"
                  }`}
                >
                  <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={tags.includes(t)} 
                    onChange={(e) => setTags((s) => e.target.checked ? [...s, t] : s.filter((x) => x !== t))} 
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="ess-section-label block mb-1">Date</label>
              <input 
                type="date" 
                value={date} 
                onChange={(e) => setDate(e.target.value)} 
                className="w-full border border-gray-200 px-2 py-2 text-sm" 
                data-testid="r-edit-date" 
              />
            </div>
            <div>
              <label className="ess-section-label block mb-1">Start</label>
              <input 
                type="time" 
                value={start} 
                onChange={(e) => setStart(e.target.value)} 
                className="w-full border border-gray-200 px-2 py-2 text-sm" 
                data-testid="r-edit-start" 
              />
            </div>
            <div>
              <label className="ess-section-label block mb-1">End</label>
              <input 
                type="time" 
                value={end} 
                onChange={(e) => setEnd(e.target.value)} 
                className="w-full border border-gray-200 px-2 py-2 text-sm" 
                data-testid="r-edit-end" 
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="border border-gray-200 px-4 py-2 text-sm">Cancel</button>
            <button 
              type="submit" 
              disabled={saving} 
              className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider" 
              data-testid="r-edit-save"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WeeklyView({ data }) {
  // Build pivot: rows = users, columns = weeks (grouped by ISO week), cells = hours per day within week
  // Simpler: rows = users, columns = each day in range, values = duration_seconds
  //const entries = data?.entries || [];
  const entries = (data?.entries || []).filter(e => e.project_name && e.project_name.trim() !== "");
  const userMap = {};
  const dayMap = {};
  for (const e of entries) {
    const localDate = getLocalDateFromTimestamp(e.start_time) || e.date;
    if (!userMap[e.user_id]) userMap[e.user_id] = { user_name: e.user_name, user_email: e.user_email, total: 0, days: {} };
    userMap[e.user_id].days[localDate] = (userMap[e.user_id].days[localDate] || 0) + e.duration_seconds;
    userMap[e.user_id].total += e.duration_seconds;
    dayMap[localDate] = (dayMap[localDate] || 0) + e.duration_seconds;
  }
  const days = Object.keys(dayMap).sort();
  const users = Object.values(userMap).sort((a, b) => b.total - a.total);
  const grandTotal = Object.values(dayMap).reduce((s, v) => s + v, 0);

  if (days.length === 0) {
    return <div className="border border-gray-200 p-10 text-center text-gray-500">No entries for this range.</div>;
  }

  return (
    <div className="border border-gray-200 overflow-x-auto" data-testid="weekly-view">
      <table className="ess-table w-full min-w-[800px]">
        <thead>
          <tr>
            <th className="sticky left-0 bg-[var(--ess-surface)]">Team Member</th>
            {days.map((d) => (
              <th key={d} className="text-right whitespace-nowrap text-[10px]">
                {new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </th>
            ))}
            <th className="text-right bg-[var(--ess-surface)]">Total</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={i} data-testid={`weekly-row-${i}`}>
              <td className="sticky left-0 bg-white font-medium text-[var(--ess-ink)]">
                <div>{u.user_name}</div>
                <div className="text-xs text-gray-500 font-normal">{u.user_email}</div>
              </td>
              {days.map((d) => {
                const v = u.days[d] || 0;
                return (
                  <td key={d} className={`text-right tabular-nums text-xs ${v === 0 ? "text-gray-300" : ""}`}>
                    {v > 0 ? `${(v / 3600).toFixed(2)}h` : "—"}
                  </td>
                );
              })}
              <td className="text-right tabular-nums font-semibold text-[var(--ess-ink)]">{(u.total / 3600).toFixed(2)}h</td>
            </tr>
          ))}
          <tr className="bg-[var(--ess-surface)]">
            <td className="font-semibold sticky left-0 bg-[var(--ess-surface)]">Daily Total</td>
            {days.map((d) => (
              <td key={d} className="text-right tabular-nums text-xs font-semibold">{((dayMap[d] || 0) / 3600).toFixed(2)}h</td>
            ))}
            <td className="text-right tabular-nums font-bold text-[var(--ess-ink)]">{(grandTotal / 3600).toFixed(2)}h</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
